(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-mbb-universe]';
  const instances = new WeakMap();

  const ZONE_NAMES = {
    france: {
      'FR-BRE': 'Bretagne',
      'FR-NOR': 'Normandie',
      'FR-HDF': 'Hauts-de-France',
      'FR-IDF': 'Île-de-France',
      'FR-CVL': 'Centre-Val de Loire',
      'FR-PDL': 'Pays de la Loire',
      'FR-BFC': 'Bourgogne-Franche-Comté',
      'FR-GES': 'Grand Est',
      'FR-NAQ': 'Nouvelle-Aquitaine',
      'FR-ARA': 'Auvergne-Rhône-Alpes',
      'FR-OCC': 'Occitanie',
      'FR-PAC': 'Provence-Alpes-Côte d\'Azur',
      'FR-COR': 'Corse'
    },
    world: {
      MA: 'Maroc',
      FR: 'France',
      ES: 'Espagne',
      IT: 'Italie',
      PT: 'Portugal',
      GR: 'Grèce',
      NO: 'Norvège',
      JP: 'Japon',
      US: 'États-Unis',
      CA: 'Canada',
      AU: 'Australie'
    }
  };

  const normalizeZone = value => String(value || '').trim().toUpperCase();

  const buildAvailableZoneSet = destinations =>
    new Set([...destinations.keys()].filter(Boolean));

  const cssEscape = value => {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, '\\$&');
  };

  const reducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const getHashUniverse = () => {
    let hash = String(window.location.hash || '').replace(/^#/, '').toLowerCase();

    try {
      hash = decodeURIComponent(hash);
    } catch (error) {
      return '';
    }

    if (hash === 'france') {
      return 'france';
    }

    if (hash === 'monde') {
      return 'world';
    }

    if (hash === 'universe') {
      return 'universe';
    }

    return '';
  };

  class Tabs {
    constructor(section, onChange) {
      this.section = section;
      this.onChange = onChange;
      this.tabs = [...section.querySelectorAll('[data-universe-tab]')];
      this.panels = [...section.querySelectorAll('[data-universe-panel]')];

      this.bind();
    }

    bind() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          this.activate(tab.dataset.universeTab, { focus: false });
        });

        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            return;
          }

          event.preventDefault();

          let nextIndex = index;

          if (event.key === 'ArrowRight') {
            nextIndex = (index + 1) % this.tabs.length;
          }

          if (event.key === 'ArrowLeft') {
            nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
          }

          if (event.key === 'Home') {
            nextIndex = 0;
          }

          if (event.key === 'End') {
            nextIndex = this.tabs.length - 1;
          }

          const nextTab = this.tabs[nextIndex];

          this.activate(nextTab.dataset.universeTab, { focus: true });
        });
      });
    }

    activate(name, options = {}) {
      const activeName = String(name || 'france');

      this.tabs.forEach(tab => {
        const isActive = tab.dataset.universeTab === activeName;

        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;

        if (isActive && options.focus) {
          tab.focus();
        }
      });

      this.panels.forEach(panel => {
        const isActive = panel.dataset.universePanel === activeName;

        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });

      if (options.emit !== false && typeof this.onChange === 'function') {
        this.onChange(activeName);
      }
    }
  }

  class FeaturedCard {
    constructor(card) {
      this.card = card;
      this.universe = card.dataset.featuredUniverse;
      this.image = card.querySelector('[data-featured-image]');
      this.placeholder = card.querySelector('[data-featured-placeholder]');
      this.badge = card.querySelector('[data-featured-badge]');
      this.eyebrow = card.querySelector('[data-featured-eyebrow]');
      this.title = card.querySelector('[data-featured-title]');
      this.description = card.querySelector('[data-featured-description]');
      this.comparePrice = card.querySelector('[data-featured-compare-price]');
      this.price = card.querySelector('[data-featured-price]');
      this.button = card.querySelector('[data-featured-button]');
      this.buttonLabel = card.querySelector('[data-featured-button-label]');
      this.benefits = [...card.querySelectorAll('[data-featured-benefit]')];
    }

    update(destination) {
      if (!destination) {
        this.card.setAttribute('aria-busy', 'false');
        return;
      }

      this.card.setAttribute('aria-busy', 'true');
      this.card.dataset.activeZone = destination.zone_id;
      this.card.classList.add('is-updating');

      this.updateImage(destination);
      this.setText(this.badge, destination.badge);
      this.setText(this.eyebrow, destination.eyebrow);
      this.setText(this.title, destination.title);
      this.setText(this.description, destination.description);
      this.updateBenefits(destination.benefits || []);
      this.setText(this.comparePrice, destination.compare_price);
      this.setText(this.price, destination.price);
      this.updateButton(destination);

      const finish = () => {
        this.card.classList.remove('is-updating');
        this.card.classList.add('is-ready');
        this.card.setAttribute('aria-busy', 'false');
      };

      if (reducedMotion()) {
        finish();
        return;
      }

      window.requestAnimationFrame(() => {
        window.setTimeout(finish, 120);
      });
    }

    updateImage(destination) {
      const hasImage = Boolean(destination.image);

      if (this.image) {
        if (hasImage) {
          this.image.src = destination.image;
          this.image.alt = destination.image_alt || destination.title || '';
          this.image.hidden = false;
        } else {
          this.image.removeAttribute('src');
          this.image.alt = '';
          this.image.hidden = true;
        }
      }

      if (this.placeholder) {
        this.placeholder.hidden = hasImage;
      }
    }

    updateBenefits(benefits) {
      this.benefits.forEach((benefit, index) => {
        const value = benefits[index] || '';
        const item = benefit.closest('[data-featured-benefit-item]');

        benefit.textContent = value;

        if (item) {
          item.hidden = value === '';
        }
      });
    }

    updateButton(destination) {
      const label = destination.button_label || '';
      const link = destination.button_link || '';

      if (this.buttonLabel) {
        this.buttonLabel.textContent = label;
      }

      if (!this.button) {
        return;
      }

      this.button.hidden = label === '';
      this.button.href = link || '#';
      this.button.setAttribute('aria-disabled', String(link === ''));
      this.button.tabIndex = link === '' ? -1 : 0;
    }

    setText(element, value) {
      if (!element) {
        return;
      }

      const text = value || '';

      element.textContent = text;
      element.hidden = text === '';
    }
  }

  class BaseMap {
    constructor(container, options) {
      this.container = container;
      this.universe = options.universe;
      this.svgClass = options.svgClass;
      this.zoneClass = options.zoneClass;
      this.mapLabel = options.mapLabel;
      this.names = options.names || {};
      this.regionNames = typeof Intl.DisplayNames === 'function'
        ? new Intl.DisplayNames([document.documentElement.lang || 'fr'], { type: 'region' })
        : null;
      this.discoverZoneLabel = options.discoverZoneLabel || '';
      this.removeZoneIds = options.removeZoneIds || [];
      this.viewBox = options.viewBox || '';
      this.canvas = container.querySelector('[data-map-canvas]');
      this.viewport = container.querySelector('[data-map-viewport]');
      this.loader = container.querySelector('[data-map-loader]');
      this.tooltip = container.querySelector('[data-map-tooltip]');
      this.error = container.querySelector('[data-map-error]');
      this.activeLabel = container.querySelector('[data-map-active-label]');
      this.svgUrl = container.dataset.mapSvgUrl;
      this.defaultZone = normalizeZone(options.defaultZone || container.dataset.defaultZone);
      this.availableZones = options.availableZones instanceof Set ? options.availableZones : new Set();
      this.zoneElements = new Map();
      this.focusableZones = [];
      this.activeZone = '';

      this.load();
    }

    async load() {
      if (!this.svgUrl || !this.canvas) {
        const cause = !this.svgUrl
          ? 'missing data-map-svg-url on map container'
          : 'missing [data-map-canvas] inside map container';
        console.error(`[MBB ${this.universe} map] Unable to initialize SVG`, {
          url: this.svgUrl || '(empty)',
          cause,
          container: this.container
        });
        this.fail();
        return;
      }

      let stage = 'fetch';
      let response;

      try {
        response = await fetch(this.svgUrl, { credentials: 'same-origin' });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status} ${response.statusText || ''}`.trim());
        }

        stage = 'read response body';
        const source = await response.text();
        if (!source.trim()) {
          throw new Error('The response body is empty');
        }

        stage = 'parse SVG XML';
        const parser = new DOMParser();
        const svgDocument = parser.parseFromString(source, 'image/svg+xml');

        const parserError = svgDocument.querySelector('parsererror');
        if (parserError) {
          throw new Error(`Invalid SVG XML: ${parserError.textContent.trim()}`);
        }

        const svg = svgDocument.querySelector('svg');

        if (!svg) {
          throw new Error('Missing SVG element');
        }

        stage = 'prepare SVG zones';
        this.prepareSvg(svg);
        stage = 'inject SVG into map canvas';
        this.mountSvg(svg);
        stage = 'bind map interactions';
        this.bindZones();
        this.setActive(this.defaultZone, { emit: false });
        this.ready();
      } catch (error) {
        console.error(`[MBB ${this.universe} map] Failed to load map`, {
          url: this.svgUrl,
          stage,
          httpStatus: response?.status ?? null,
          cause: error?.message || String(error),
          error
        });
        this.fail();
      }
    }

    prepareSvg(svg) {
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.removeAttribute('style');
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', this.mapLabel);
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.classList.add('mbb-map__svg', this.svgClass);

      svg.querySelectorAll('script, style, foreignObject').forEach(element => {
        element.remove();
      });

      this.removeZoneIds.forEach(zoneId => {
        svg.querySelector(`#${cssEscape(zoneId)}`)?.remove();
      });

      if (this.viewBox) {
        svg.setAttribute('viewBox', this.viewBox);
      }

      svg
        .querySelectorAll('rect, .ocean, #ocean, .background, #background, [data-background]')
        .forEach(element => {
          element.remove();
        });

      svg.querySelectorAll('path, polygon, g, [style]').forEach(element => {
        element.removeAttribute('fill');
        element.removeAttribute('stroke');
        element.removeAttribute('style');

        if (element.matches('path, polygon')) {
          element.setAttribute('vector-effect', 'non-scaling-stroke');
        }
      });

      svg.querySelectorAll('path[id], polygon[id]').forEach(element => {
        const zoneId = normalizeZone(element.id);

        this.prepareZoneElement(element, zoneId);
      });
    }

    mountSvg(svg) {
      const currentSvg = this.canvas.querySelector('svg');

      if (currentSvg) {
        this.bindZones();
        return;
      }

      this.canvas.appendChild(svg);
    }

    prepareZoneElement(element, zoneId) {
      const zoneName = this.getZoneName(zoneId, element);

      element.classList.remove(
        'land',
        'mbb-map__zone',
        'mbb-map-zone--available',
        'mbb-map-zone--unavailable',
        'is-active',
        'is-available',
        'is-unavailable'
      );
      element.classList.add('mbb-map__shape', this.zoneClass);
      element.dataset.mapZone = zoneId;
      element.dataset.zoneName = zoneName;

      element.querySelectorAll('path, polygon').forEach(child => {
        child.setAttribute('vector-effect', 'non-scaling-stroke');
      });

      if (!this.availableZones.has(zoneId)) {
        this.prepareUnavailableZoneElement(element);
        return;
      }

      this.prepareAvailableZoneElement(element, zoneName);
    }

    prepareAvailableZoneElement(element, zoneName) {
      element.classList.add('mbb-map__zone', 'mbb-map-zone--available', 'is-available');
      element.classList.remove('mbb-map-zone--unavailable', 'is-unavailable');
      element.removeAttribute('aria-disabled');
      element.setAttribute('role', 'button');
      element.setAttribute('tabindex', '0');
      element.setAttribute('aria-label', `${this.discoverZoneLabel} ${zoneName}`.trim());
      element.setAttribute('aria-pressed', 'false');
    }

    prepareUnavailableZoneElement(element) {
      element.classList.add('mbb-map-zone--unavailable', 'is-unavailable');
      element.classList.remove('mbb-map__zone', 'mbb-map-zone--available', 'is-available');
      element.removeAttribute('role');
      element.removeAttribute('tabindex');
      element.removeAttribute('aria-label');
      element.removeAttribute('aria-pressed');
      element.setAttribute('aria-disabled', 'true');
    }

    bindZones() {
      this.zoneElements.clear();

      const elements = [
        ...this.canvas.querySelectorAll('[data-map-zone].is-available')
      ].filter(element => this.availableZones.has(normalizeZone(element.dataset.mapZone)));

      elements.forEach(element => {
        const zoneId = normalizeZone(element.dataset.mapZone);

        this.zoneElements.set(zoneId, element);
        this.addZoneListeners(element, zoneId);
      });

      this.focusableZones = elements;
    }

    addZoneListeners(element, zoneId) {
      element.addEventListener('click', () => {
        this.setActive(zoneId, { emit: true, source: 'click' });
      });

      element.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.setActive(zoneId, { emit: true, source: 'keyboard' });
          return;
        }

        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
          event.preventDefault();
          this.focusSibling(element, event.key);
        }
      });

      element.addEventListener('pointerenter', event => {
        this.showTooltip(event, zoneId);
      });

      element.addEventListener('pointermove', event => {
        this.moveTooltip(event);
      });

      element.addEventListener('pointerleave', () => {
        this.hideTooltip();
      });

      element.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch') {
          this.showTooltip(event, zoneId);
        }
      });

      element.addEventListener('focus', () => {
        this.showTooltipForElement(element, zoneId);
      });

      element.addEventListener('blur', () => {
        this.hideTooltip();
      });
    }

    focusSibling(element, key) {
      const index = this.focusableZones.indexOf(element);

      if (index < 0) {
        return;
      }

      const direction = key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 1;
      const nextIndex = (index + direction + this.focusableZones.length) % this.focusableZones.length;

      this.focusableZones[nextIndex]?.focus();
    }

    setActive(zoneId, options = {}) {
      const zone = normalizeZone(zoneId);
      const element = this.zoneElements.get(zone);

      if (!element) {
        return false;
      }

      this.zoneElements.forEach(zoneElement => {
        const isActive = zoneElement === element;

        zoneElement.classList.toggle('is-active', isActive);
        zoneElement.setAttribute('aria-pressed', String(isActive));
      });

      this.activeZone = zone;
      this.container.dataset.activeZone = zone;
      this.updateActiveLabel(zone);

      if (options.emit !== false) {
        this.container.dispatchEvent(
          new CustomEvent('mbb:map-change', {
            bubbles: true,
            detail: {
              universe: this.universe,
              zone,
              zoneId: zone,
              name: this.getZoneName(zone, element),
              source: options.source || 'map'
            }
          })
        );
      }

      return true;
    }

    updateActiveLabel(zoneId) {
      if (!this.activeLabel) {
        return;
      }

      const zoneName = zoneId ? this.getZoneName(zoneId) : '';

      this.activeLabel.textContent = zoneName;
      this.activeLabel.hidden = zoneName === '';
    }

    showTooltip(event, zoneId) {
      if (!this.tooltip) {
        return;
      }

      this.tooltip.textContent = this.getZoneName(zoneId, event.currentTarget);
      this.tooltip.hidden = false;
      this.moveTooltip(event);
    }

    showTooltipForElement(element, zoneId) {
      if (!this.tooltip || !this.container) {
        return;
      }

      const containerRect = this.container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      this.tooltip.textContent = this.getZoneName(zoneId, element);
      this.tooltip.hidden = false;
      this.tooltip.style.left = `${elementRect.left - containerRect.left + elementRect.width / 2}px`;
      this.tooltip.style.top = `${elementRect.top - containerRect.top}px`;
    }

    moveTooltip(event) {
      if (!this.tooltip || this.tooltip.hidden) {
        return;
      }

      const containerRect = this.container.getBoundingClientRect();

      this.tooltip.style.left = `${event.clientX - containerRect.left}px`;
      this.tooltip.style.top = `${event.clientY - containerRect.top}px`;
    }

    hideTooltip() {
      if (this.tooltip) {
        this.tooltip.hidden = true;
      }
    }

    getZoneName(zoneId, element) {
      const zone = normalizeZone(zoneId);
      const isIntlRegionCode = /^(?:[A-Z]{2}|\d{3})$/.test(zone);

      return (
        (isIntlRegionCode ? this.regionNames?.of(zone) : '') ||
        this.names[zone] ||
        element?.dataset.zoneName ||
        element?.getAttribute('title') ||
        zone
      );
    }

    ready() {
      if (this.loader) {
        this.loader.hidden = true;
      }

      if (this.viewport) {
        this.viewport.setAttribute('aria-busy', 'false');
      }

      this.container.classList.add('is-ready');
    }

    fail() {
      if (this.loader) {
        this.loader.hidden = true;
      }

      if (this.viewport) {
        this.viewport.setAttribute('aria-busy', 'false');
      }

      if (this.error) {
        this.error.hidden = false;
      }

      this.container.classList.add('has-error');
    }
  }

  class FranceMap extends BaseMap {
    constructor(container, options = {}) {
      super(container, {
        universe: 'france',
        svgClass: 'mbb-map-france__svg',
        zoneClass: 'mbb-map-france__region',
        mapLabel: container.closest('[data-mbb-universe]')?.dataset.mapLabelFrance,
        discoverZoneLabel: container.closest('[data-mbb-universe]')?.dataset.discoverZoneLabel,
        names: ZONE_NAMES.france,
        availableZones: options.availableZones,
        defaultZone: options.defaultZone
      });
    }
  }

  class WorldMap extends BaseMap {
    constructor(container, options = {}) {
      super(container, {
        universe: 'world',
        svgClass: 'mbb-map-world__svg',
        zoneClass: 'mbb-map-world__country',
        mapLabel: container.closest('[data-mbb-universe]')?.dataset.mapLabelWorld,
        discoverZoneLabel: container.closest('[data-mbb-universe]')?.dataset.discoverZoneLabel,
        names: ZONE_NAMES.world,
        availableZones: options.availableZones,
        defaultZone: options.defaultZone,
        removeZoneIds: ['AQ'],
        viewBox: '12.99 24 1638.01 730'
      });
    }
  }

  class DestinationUniverse {
    constructor(section) {
      this.section = section;
      this.payload = this.readPayload();
      this.destinations = this.groupDestinations(this.payload.destinations || []);
      this.availableFranceZones = buildAvailableZoneSet(this.destinations.france);
      this.availableWorldZones = buildAvailableZoneSet(this.destinations.world);
      this.availableZones = {
        france: this.availableFranceZones,
        world: this.availableWorldZones
      };
      this.defaults = {
        tab: this.payload.defaults?.tab || section.dataset.defaultTab || 'france',
        france: this.resolveDefaultZone(
          'france',
          normalizeZone(this.payload.defaults?.france || section.dataset.defaultFranceZone || 'FR-BFC')
        ),
        world: this.resolveDefaultZone(
          'world',
          normalizeZone(this.payload.defaults?.world || section.dataset.defaultWorldZone || 'MA')
        )
      };
      this.activeZones = {
        france: this.defaults.france,
        world: this.defaults.world
      };
      this.cards = new Map();
      this.maps = new Map();
      this.onHashChange = () => {
        this.applyUrlHash({ scroll: true });
      };

      this.initCards();
      this.initTabs();
      this.initMaps();
      this.bindEvents();
      this.initObserver();
      this.hydrate();
      this.bindHashEvents();
      this.applyUrlHash({ scroll: true });
    }

    readPayload() {
      const script = this.section.querySelector('[data-mbb-universe-data]');

      if (!script) {
        return {};
      }

      try {
        return JSON.parse(script.textContent || '{}');
      } catch (error) {
        console.error('[MBB destinations] Invalid JSON payload', error);
        return {};
      }
    }

    groupDestinations(destinations) {
      return destinations.reduce(
        (groups, destination) => {
          const universe = destination.universe === 'world' ? 'world' : 'france';
          const zone = normalizeZone(destination.zone_id);

          if (!zone) {
            return groups;
          }

          groups[universe].set(zone, {
            ...destination,
            universe,
            zone_id: zone,
            benefits: (destination.benefits || []).filter(Boolean)
          });

          return groups;
        },
        {
          france: new Map(),
          world: new Map()
        }
      );
    }

    resolveDefaultZone(universe, preferredZone) {
      const group = this.destinations[universe];

      if (!group || group.size === 0) {
        return preferredZone;
      }

      if (group.has(preferredZone)) {
        return preferredZone;
      }

      return group.keys().next().value || preferredZone;
    }

    initCards() {
      this.section.querySelectorAll('[data-destination-featured]').forEach(card => {
        const featured = new FeaturedCard(card);
        this.cards.set(featured.universe, featured);
      });
    }

    initTabs() {
      this.tabs = new Tabs(this.section, tabName => {
        this.handleTabChange(tabName);
      });
    }

    initMaps() {
      const franceMap = this.section.querySelector('[data-mbb-map="france"]');
      const worldMap = this.section.querySelector('[data-mbb-map="world"]');

      if (franceMap) {
        this.maps.set('france', new FranceMap(franceMap, {
          availableZones: this.availableFranceZones,
          defaultZone: this.defaults.france
        }));
      }

      if (worldMap) {
        this.maps.set('world', new WorldMap(worldMap, {
          availableZones: this.availableWorldZones,
          defaultZone: this.defaults.world
        }));
      }
    }

    bindEvents() {
      this.section.addEventListener('mbb:map-change', event => {
        const detail = event.detail || {};
        const universe = detail.universe === 'world' ? 'world' : 'france';
        const zoneId = detail.zoneId || detail.zone;

        this.selectDestination(universe, zoneId, { updateMap: false });
      });
    }

    bindHashEvents() {
      window.addEventListener('hashchange', this.onHashChange);
    }

    initObserver() {
      if (!('IntersectionObserver' in window)) {
        this.section.classList.add('is-visible');
        return;
      }

      this.observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.section.classList.add('is-visible');
              this.observer.disconnect();
            }
          });
        },
        { threshold: 0.24 }
      );

      this.observer.observe(this.section);
    }

    hydrate() {
      this.tabs.activate(this.defaults.tab, { emit: false });
      this.selectDestination('france', this.defaults.france, { updateMap: false });
      this.selectDestination('world', this.defaults.world, { updateMap: false });
    }

    applyUrlHash(options = {}) {
      const universe = getHashUniverse();

      if (!universe) {
        return false;
      }

      if (universe === 'universe') {
        if (options.scroll) {
          this.scrollToSection();
        }

        return true;
      }

      this.tabs.activate(universe, { focus: false });

      if (options.scroll) {
        this.scrollToSection();
      }

      return true;
    }

    scrollToSection() {
      this.section.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

    handleTabChange(universe) {
      const activeZone = this.maps.get(universe)?.activeZone || this.activeZones[universe];

      this.selectDestination(universe, activeZone, {
        updateMap: true,
        emitMap: false
      });
    }

    selectDestination(universe, zoneId, options = {}) {
      const zone = normalizeZone(zoneId);
      const destination = this.getDestination(universe, zone);

      if (!destination) {
        return;
      }

      this.activeZones[universe] = destination.zone_id;
      this.cards.get(universe)?.update(destination);

      if (options.updateMap) {
        this.maps.get(universe)?.setActive(destination.zone_id, {
          emit: options.emitMap !== false,
          source: 'universe'
        });
      }
    }

    getDestination(universe, zoneId) {
      const group = this.destinations[universe];

      if (!group || group.size === 0) {
        return null;
      }

      return group.get(normalizeZone(zoneId)) || group.values().next().value || null;
    }

    destroy() {
      this.observer?.disconnect();
      window.removeEventListener('hashchange', this.onHashChange);
      instances.delete(this.section);
      delete this.section.dataset.mbbInitialized;
    }
  }

  const initSection = section => {
    if (!section || instances.has(section)) {
      return;
    }

    section.dataset.mbbInitialized = 'true';
    instances.set(section, new DestinationUniverse(section));
  };

  const initAll = root => {
    const scope = root || document;

    if (scope.matches?.(SECTION_SELECTOR)) {
      initSection(scope);
    }

    scope.querySelectorAll?.(SECTION_SELECTOR).forEach(initSection);
  };

  const destroyAll = root => {
    const scope = root || document;
    const sections = [];

    if (scope.matches?.(SECTION_SELECTOR)) {
      sections.push(scope);
    }

    scope.querySelectorAll?.(SECTION_SELECTOR).forEach(section => {
      sections.push(section);
    });

    sections.forEach(section => {
      instances.get(section)?.destroy();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', event => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', event => {
    destroyAll(event.target);
  });
})();
